import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import FormSelect from '@Components/Form/FormSelect';
import FormTextAreaAutoSize from '@Components/Form/FormTextAreaAutosize';
import {
  disabledFormInputStyles,
  disabledTextAreaInputStyles,
} from '@Components/Form/styles';
import { InputLabel, MenuItem } from '@mui/material';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useNatureOfBusinessForm } from '../NatureOfBusinessFormProvider';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';

const GeneralInformationForm = () => {
  const [{ data: provinces = [] }] = useGetInfoCategoryMasterData(
    INFO_CATEGORY_MASTER_DATA.PROVINCES
  );

  const { isCompletedTh, isCompletedEn } =
    useWatch({
      name: 'corporateInformation.completedSection',
    }) ?? ({} as CompleteSectionFormValues);
  const provinceTh = useWatch({
    name: 'corporateInformation.thai.provinceTh',
  });
  const phoneNumber = useWatch({
    name: 'corporateInformation.thai.telephoneTh',
  });

  const { setValue } = useNatureOfBusinessForm();

  const [telephoneTh, setTelephoneTh] = useState<string>(phoneNumber);
  const [telephoneEn, setTelephoneEn] = useState<string>(phoneNumber);

  const convertProvinceThToEng = (provinceTh: string): string | null => {
    return (
      provinces.find((province) => province.nameTh === provinceTh)?.nameEn ||
      null
    );
  };

  useEffect(() => {
    if (provinceTh) {
      setValue(
        'corporateInformation.english.provinceEn',
        convertProvinceThToEng(provinceTh) ?? ''
      );
    }
  }, [provinces, provinceTh, setValue]);

  return (
    <Flex flexDirection={'column'} gap={3} px={2} marginBottom={4}>
      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{
            textAlign: 'right',
            minWidth: '350px',
            paddingRight: 1,
            marginTop: 2,
          }}
          required
        >
          ชื่อบริษัท
        </InputLabel>
        <FormTextAreaAutoSize
          width={'100%'}
          name="corporateInformation.thai.companyTh"
          label="ภาษาไทย"
          inputProps={{
            minRows: 3,
            placeholder: 'กรุณาระบุชื่อบริษัท',
            readOnly: isCompletedTh,
          }}
          sx={isCompletedTh ? disabledTextAreaInputStyles : {}}
        />
        <FormTextAreaAutoSize
          width={'100%'}
          name="corporateInformation.english.companyEn"
          label="ภาษาอังกฤษ"
          inputProps={{
            minRows: 3,
            placeholder: 'กรุณาระบุชื่อบริษัท',
            readOnly: isCompletedEn,
          }}
          sx={isCompletedEn ? disabledTextAreaInputStyles : {}}
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          ชื่อย่อหลักทรัพย์
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.symbolTh"
          placeholder="กรุณาระบุชื่อย่อหลักทรัพย์"
          fullWidth
          onChange={(event) => {
            const value = event.target.value;
            setValue('corporateInformation.thai.symbolTh', value);
            setValue('corporateInformation.english.symbolEn', value);
          }}
          required
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.symbolEn"
          placeholder="กรุณาระบุชื่อย่อหลักทรัพย์"
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
          required
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          สถานที่ตั้งสำนักงานใหญ่
        </InputLabel>
        <FormTextAreaAutoSize
          width={'100%'}
          name="corporateInformation.thai.addressTh"
          inputProps={{
            minRows: 3,
            placeholder: 'กรุณาระบุสถานที่ตั้งสำนักงานใหญ่',
            readOnly: isCompletedTh,
          }}
          sx={isCompletedTh ? disabledTextAreaInputStyles : {}}
        />
        <FormTextAreaAutoSize
          width={'100%'}
          name="corporateInformation.english.addressEn"
          inputProps={{
            minRows: 3,
            placeholder: 'กรุณาระบุสถานที่ตั้งสำนักงานใหญ่',
            readOnly: isCompletedEn,
          }}
          sx={isCompletedEn ? disabledTextAreaInputStyles : {}}
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          จังหวัด
        </InputLabel>
        <FormSelect
          name="corporateInformation.thai.provinceTh"
          required
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          value={provinceTh ?? ''}
          // TODO cannot disable
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        >
          <MenuItem value="" disabled>
            กรุณาระบุจังหวัด
          </MenuItem>
          {provinces.map((province) => (
            <MenuItem key={province.nameEn} value={province.nameTh}>
              {province.nameTh}
            </MenuItem>
          ))}
        </FormSelect>
        <FormInput
          name="corporateInformation.english.provinceEn"
          placeholder="กรุณาระบุจังหวัด"
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
          required
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          รหัสไปรษณีย์
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.zipcodeTh"
          placeholder="กรุณาระบุรหัสไปรษณีย์"
          inputProps={{
            maxLength: 5,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          fullWidth
          required
          onChange={(event) => {
            const value = event.target.value;
            setValue('corporateInformation.thai.zipcodeTh', value);
            setValue('corporateInformation.english.zipcodeEn', value);
          }}
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.zipcodeEn"
          placeholder="กรุณาระบุรหัสไปรษณีย์"
          fullWidth
          required
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          ประเภทธุรกิจ
        </InputLabel>
        <FormTextAreaAutoSize
          width={'100%'}
          name="corporateInformation.thai.businessTypeTh"
          inputProps={{
            minRows: 3,
            placeholder: 'กรุณาระบุประเภทธุรกิจ',
            readOnly: isCompletedTh,
          }}
          sx={isCompletedTh ? disabledTextAreaInputStyles : {}}
        />
        <FormTextAreaAutoSize
          width={'100%'}
          name="corporateInformation.english.businessTypeEn"
          inputProps={{
            minRows: 3,
            placeholder: 'กรุณาระบุประเภทธุรกิจ',
            readOnly: isCompletedEn,
          }}
          sx={isCompletedEn ? disabledTextAreaInputStyles : {}}
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          เลขทะเบียนบริษัท
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.registeredNumberTh"
          placeholder="กรุณาระบุเลขทะเบียนบริษัท"
          fullWidth
          required
          onChange={(event) => {
            const value = event.target.value;
            setValue('corporateInformation.thai.registeredNumberTh', value);
            setValue('corporateInformation.english.registeredNumberEn', value);
          }}
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.registeredNumberEn"
          placeholder="กรุณาระบุเลขทะเบียนบริษัท"
          fullWidth
          required
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          โทรศัพท์
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.telephoneTh"
          placeholder="กรุณาระบุโทรศัพท์"
          fullWidth
          value={telephoneTh}
          onChange={(event) => {
            const value = event.target.value;
            setTelephoneTh(value);
            setTelephoneEn(value);
            setValue('corporateInformation.thai.telephoneTh', value);
            setValue('corporateInformation.english.telephoneEn', value);
          }}
          required
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.telephoneEn"
          placeholder="กรุณาระบุโทรศัพท์"
          fullWidth
          value={telephoneEn}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
          required
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
        >
          โทรสาร (ถ้ามี)
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.faxTh"
          placeholder="กรุณาระบุโทรสาร"
          fullWidth
          onChange={(event) => {
            const value = event.target.value;
            setValue('corporateInformation.thai.faxTh', value);
            setValue('corporateInformation.english.faxEn', value);
          }}
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.faxEn"
          placeholder="กรุณาระบุโทรสาร"
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          เว็บไซต์
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.websiteTh"
          placeholder="กรุณาระบุเว็บไซต์"
          fullWidth
          onChange={(event) => {
            const value = event.target.value;
            setValue('corporateInformation.thai.websiteTh', value);
            setValue('corporateInformation.english.websiteEn', value);
          }}
          required
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.websiteEn"
          placeholder="กรุณาระบุเว็บไซต์"
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
          required
        />
      </Flex>

      <Flex flexDirection={'row'} gap={2} width={'100%'}>
        <InputLabel
          sx={{ textAlign: 'right', minWidth: '350px', paddingRight: 1 }}
          required
        >
          อีเมล
        </InputLabel>
        <FormInput
          name="corporateInformation.thai.emailTh"
          placeholder="กรุณาระบุอีเมล"
          fullWidth
          onChange={(event) => {
            const value = event.target.value;
            setValue('corporateInformation.thai.emailTh', value);
            setValue('corporateInformation.english.emailEn', value);
          }}
          slotProps={{
            input: {
              readOnly: isCompletedTh || isCompletedEn,
            },
          }}
          sx={isCompletedTh || isCompletedEn ? disabledFormInputStyles : {}}
        />
        <FormInput
          name="corporateInformation.english.emailEn"
          placeholder="กรุณาระบุอีเมล"
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={
            isCompletedEn || isCompletedTh
              ? disabledFormInputStyles
              : { pointerEvents: 'none' }
          }
          required
        />
      </Flex>
    </Flex>
  );
};

export default GeneralInformationForm;
